import { useEffect, useMemo, useState } from "react";
import { FiImage, FiSearch } from "react-icons/fi";
import GridLoader from "react-spinners/GridLoader";
import type {
  RemoteVendorProduct,
  RemoteVendorProductVariant,
  Vendor,
} from "../../../../features/interfaces";
import { useLazyGetVendorRemoteProductsQuery } from "../../../../features/vendors/vendorApi";
import { filterRemoteProducts } from "../../../vendors/components/vendorRemoteProducts/filterRemoteProducts";
import {
  hasVendorIntegration,
  isVendorIntegrationActive,
} from "../../../vendors/components/vendorRemoteProducts/hasVendorIntegration";
import {
  mapRemoteSelectionToFormFields,
  type VendorRemoteMappingSelection,
} from "./mapRemoteSelection";

type VendorRemoteProductPickerProps = {
  vendor: Vendor | null | undefined;
  onSelect: (selection: VendorRemoteMappingSelection) => void;
};

const RemoteProductThumb = ({
  src,
  alt,
}: {
  src?: string | null;
  alt: string;
}) => {
  if (!src) {
    return (
      <div className="w-12 h-12 shrink-0 rounded-md bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-400">
        <FiImage size={18} />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className="w-12 h-12 shrink-0 rounded-md object-cover border border-gray-200 bg-white"
      loading="lazy"
    />
  );
};

/**
 * Search a vendor's integrated catalog by name and emit mapping fields.
 * Hidden when the vendor has no active integration.
 */
const VendorRemoteProductPicker = ({
  vendor,
  onSelect,
}: VendorRemoteProductPickerProps) => {
  const [search, setSearch] = useState("");
  const [selectedKey, setSelectedKey] = useState("");

  const [fetchRemoteProducts, { data, isFetching, isLoading, error, reset }] =
    useLazyGetVendorRemoteProductsQuery();

  const canSearch =
    isVendorIntegrationActive(vendor) && Boolean(vendor?.vendor_id);
  const hasIntegration = hasVendorIntegration(vendor);

  const products = data?.data?.products || [];

  const filtered = useMemo(
    () =>
      filterRemoteProducts(products, {
        name: search,
        status: "",
      }),
    [products, search]
  );

  useEffect(() => {
    setSearch("");
    setSelectedKey("");
    reset();

    if (!canSearch || !vendor?.vendor_id) return;

    fetchRemoteProducts(vendor.vendor_id);
  }, [vendor?.vendor_id, canSearch, fetchRemoteProducts, reset]);

  if (!hasIntegration) {
    return null;
  }

  if (!isVendorIntegrationActive(vendor)) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
        This vendor has an integration configured, but it is inactive. Activate
        it on the Vendors page to search remote products.
      </div>
    );
  }

  const handlePick = (
    product: RemoteVendorProduct,
    variant: RemoteVendorProductVariant
  ) => {
    setSelectedKey(`${product.id}:${variant.id}`);
    onSelect(mapRemoteSelectionToFormFields(product, variant));
  };

  const showResults = search.trim().length > 0;

  return (
    <div className="space-y-3 rounded-lg border border-[#e8d5b8] bg-[#faf6f0]/80 p-3">
      <div>
        <label className="text-sm font-semibold text-gray-800 mb-1.5 block">
          Search vendor catalog
        </label>
        <p className="text-xs text-gray-500 mb-2">
          Search by name, then choose a product or variant. ID, name, and URL
          fill automatically.
        </p>
        <div className="relative">
          <FiSearch
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            size={16}
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Type product name..."
            className="w-full py-2.5 pl-9 pr-4 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#dbb685] focus:border-[#dbb685] transition-all shadow-sm"
          />
        </div>
      </div>

      {(isLoading || isFetching) && (
        <div className="flex justify-center py-4">
          <GridLoader color="#dbb685" size={8} />
        </div>
      )}

      {error ? (
        <p className="text-xs text-rose-600">
          Failed to load remote products. Check vendor credentials and try again.
        </p>
      ) : null}

      {!isLoading && !isFetching && showResults && (
        <ul className="max-h-72 overflow-y-auto divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
          {filtered.length === 0 ? (
            <li className="px-3 py-3 text-xs text-gray-500">No products match.</li>
          ) : (
            filtered.slice(0, 25).map((product) => {
              const variants = product.variants?.length
                ? product.variants
                : [];
              const hasMultipleVariants = variants.length > 1;

              return (
                <li key={product.id} className="px-3 py-3">
                  <div className="flex items-start gap-3">
                    <RemoteProductThumb
                      src={product.image_url}
                      alt={product.title}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">
                            {product.title}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {variants.length} variant
                            {variants.length === 1 ? "" : "s"}
                            {product.status ? ` · ${product.status}` : ""}
                          </p>
                        </div>
                      </div>

                      {!hasMultipleVariants && variants[0] && (
                        <button
                          type="button"
                          onClick={() => handlePick(product, variants[0])}
                          className={`mt-2 w-full text-left rounded-md border px-2.5 py-1.5 text-xs transition-colors ${
                            selectedKey === `${product.id}:${variants[0].id}`
                              ? "border-[#dbb685] bg-[#f5ebe0] text-[#5c4a32]"
                              : "border-gray-200 bg-gray-50 text-gray-700 hover:border-[#dbb685] hover:bg-[#faf6f0]"
                          }`}
                        >
                          Use this product
                          {variants[0].sku ? ` · SKU ${variants[0].sku}` : ""}
                          {variants[0].price != null
                            ? ` · $${variants[0].price}`
                            : ""}
                        </button>
                      )}

                      {hasMultipleVariants && (
                        <div className="mt-2 space-y-1.5">
                          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                            Choose a variant
                          </p>
                          <div className="flex flex-col gap-1.5">
                            {variants.map((variant) => {
                              const key = `${product.id}:${variant.id}`;
                              const isSelected = selectedKey === key;
                              return (
                                <button
                                  key={variant.id}
                                  type="button"
                                  onClick={() => handlePick(product, variant)}
                                  className={`w-full text-left rounded-md border px-2.5 py-2 transition-colors ${
                                    isSelected
                                      ? "border-[#dbb685] bg-[#f5ebe0]"
                                      : "border-gray-200 bg-white hover:border-[#dbb685] hover:bg-[#faf6f0]"
                                  }`}
                                >
                                  <span className="block text-xs font-semibold text-gray-800">
                                    {variant.title || "Default"}
                                  </span>
                                  <span className="mt-0.5 block text-[11px] text-gray-500">
                                    {[
                                      variant.sku ? `SKU ${variant.sku}` : null,
                                      variant.price != null
                                        ? `$${variant.price}`
                                        : null,
                                      typeof variant.inventory_quantity ===
                                      "number"
                                        ? `Stock ${variant.inventory_quantity}`
                                        : null,
                                    ]
                                      .filter(Boolean)
                                      .join(" · ") || "No extra details"}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {!variants.length && (
                        <p className="mt-2 text-xs text-rose-500">
                          No variants available for this product.
                        </p>
                      )}
                    </div>
                  </div>
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
};

export default VendorRemoteProductPicker;
